import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Calendar, Database, Users, Lock, Mail, Globe } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  const lastUpdated = "January 26, 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Privacy Policy - MoveMate"
        description="MoveMate Privacy Policy - Learn how we collect, use, and protect your personal information."
        url="https://movemate.info/privacy"
      />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield size={40} />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-cyan-100">Last Updated: {lastUpdated}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              MoveMate ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our moving management platform, including our website, mobile applications, and related services (collectively, the "Service").
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with this policy, please do not use our Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database size={24} className="text-primary" />
              2. Information We Collect
            </h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, company name, and password when you create an account.</li>
              <li><strong>Job Information:</strong> Pickup and delivery addresses, inventory items, photos, notes, and scheduling details.</li>
              <li><strong>Payment Information:</strong> Billing address and payment card details (processed securely through our payment processor).</li>
              <li><strong>Communications:</strong> Messages you send through our chat system, support tickets, and email correspondence.</li>
              <li><strong>Employee Information:</strong> For moving company employees - name, contact details, role, and work-related information.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Device Information:</strong> Device type, operating system, browser type, and unique device identifiers.</li>
              <li><strong>Location Data:</strong> With your permission, GPS location for driver tracking and address verification.</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service, and interaction patterns.</li>
              <li><strong>Log Data:</strong> IP address, access times, and referring URLs.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">2.3 Third-Party Services</h3>
            <p className="text-gray-600 leading-relaxed">
              When you connect third-party services to MoveMate, we may receive information from those services:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li><strong>Google Calendar:</strong> Calendar event information for job scheduling synchronization.</li>
              <li><strong>Google Maps:</strong> Address validation and distance calculations.</li>
              <li><strong>Payment Processors:</strong> Transaction status and payment confirmation.</li>
            </ul>
          </section>

          {/* Google Calendar Integration */}
          <section className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar size={24} className="text-blue-600" />
              3. Google Calendar Integration
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              MoveMate offers optional integration with Google Calendar to help you manage your moving schedule. Here's what you need to know:
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.1 What We Access</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Permission to create, read, and update calendar events related to your moving jobs.</li>
              <li>Your Google account email address for identification purposes.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.2 What We Do NOT Access</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>We do not read your personal calendar events unrelated to MoveMate.</li>
              <li>We do not access your Google Drive, Gmail, or other Google services.</li>
              <li>We do not share your Google data with third parties.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.3 How to Disconnect</h3>
            <p className="text-gray-600 leading-relaxed">
              You can disconnect Google Calendar at any time from your Company Settings page. Upon disconnection, we will delete your Google authentication tokens and stop syncing calendar events.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.4 Google API Services User Data Policy</h3>
            <p className="text-gray-600 leading-relaxed">
              MoveMate's use and transfer of information received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="text-blue-600 hover:underline">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={24} className="text-primary" />
              4. How We Use Your Information
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Provide, maintain, and improve our Service.</li>
              <li>Process transactions and send related notifications.</li>
              <li>Facilitate communication between moving companies and their customers.</li>
              <li>Enable real-time driver tracking during moves.</li>
              <li>Generate estimates, invoices, and reports.</li>
              <li>Provide customer support and respond to inquiries.</li>
              <li>Send administrative information, updates, and promotional communications.</li>
              <li>Detect, prevent, and address technical issues and security threats.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Sharing</h2>
            <p className="text-gray-600 leading-relaxed mb-4">We may share your information in the following circumstances:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.1 With Moving Companies</h3>
            <p className="text-gray-600 leading-relaxed">
              If you are a customer, your job information is shared with the moving company handling your move.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.2 With Service Providers</h3>
            <p className="text-gray-600 leading-relaxed">
              We work with third-party service providers for payment processing, email delivery, cloud hosting, and analytics. These providers only access information necessary to perform their functions.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.3 For Legal Reasons</h3>
            <p className="text-gray-600 leading-relaxed">
              We may disclose information if required by law, court order, or government request, or to protect the rights, property, or safety of MoveMate, our users, or the public.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.4 Business Transfers</h3>
            <p className="text-gray-600 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Lock size={24} className="text-primary" />
              6. Data Security
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-4">
              <li>Encryption of data in transit (HTTPS/TLS) and at rest.</li>
              <li>Secure authentication with password hashing and optional two-factor authentication.</li>
              <li>Regular security assessments and monitoring.</li>
              <li>Access controls limiting employee access to personal data.</li>
              <li>Secure cloud infrastructure with reputable providers.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide our Service. We also retain information as necessary to comply with legal obligations, resolve disputes, and enforce agreements.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Job records and associated data are retained for a minimum of 7 years for legal and tax compliance purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data (subject to legal retention requirements).</li>
              <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing of your data for certain purposes.</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              To exercise these rights, please contact us at <a href="mailto:privacy@movemate.info" className="text-primary hover:underline">privacy@movemate.info</a>.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe size={24} className="text-primary" />
              9. International Data Transfers
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place for such transfers, including Standard Contractual Clauses where required.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Our Service is not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mail size={24} className="text-primary" />
              12. Contact Us
            </h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700"><strong>MoveMate</strong></p>
              <p className="text-gray-600">Email: <a href="mailto:privacy@movemate.info" className="text-primary hover:underline">privacy@movemate.info</a></p>
              <p className="text-gray-600">Website: <a href="https://movemate.info" className="text-primary hover:underline">https://movemate.info</a></p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/terms" className="text-primary hover:underline">Terms & Conditions</Link>
            <Link to="/contact" className="text-primary hover:underline">Contact Us</Link>
            <Link to="/" className="text-primary hover:underline">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
